import { useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import type { AperturaCajaRow, CajaRow, MovimientoCajaRow } from '../../types/database.types';

/**
 * Módulo de Caja (DAF v2, secciones 24 y 25): apertura con base inicial,
 * movimientos del día, y cierre con arqueo (saldo esperado vs. contado).
 * Escritura permitida a cualquier staff (admin o recepción) — misma regla
 * que las políticas RLS de cajas/aperturas_caja/movimientos_caja.
 *
 * Toda la lógica de cálculo (saldo esperado = base + ingresos - egresos,
 * diferencia = contado - esperado) vive en los RPC `abrir_caja` y
 * `cerrar_caja` (supabase/schema_gastos_caja.sql) — nunca se calcula ni
 * se confía en un total armado en el frontend para un registro
 * financiero. Las ventas y los gastos alimentan `movimientos_caja`
 * automáticamente (vía `registrar_venta` y `registrar_gasto`) mientras
 * haya una caja abierta para el salón.
 */
export function Caja() {
  const { perfil } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const {
    data: caja,
    loading: cargandoCaja,
    refetch: refetchCaja,
  } = useSupabaseQuery<CajaRow | null>(
    async () => {
      if (!perfil) return { data: null, error: null };
      const { data, error } = await supabase
        .from('cajas')
        .select('*')
        .eq('salon_id', perfil.salon_id)
        .eq('estado', 'activo')
        .maybeSingle();
      return { data, error };
    },
    [perfil],
  );

  const {
    data: apertura,
    loading: cargandoApertura,
    refetch: refetchApertura,
  } = useSupabaseQuery<AperturaCajaRow | null>(
    async () => {
      if (!caja) return { data: null, error: null };
      const { data, error } = await supabase
        .from('aperturas_caja')
        .select('*')
        .eq('caja_id', caja.id)
        .eq('estado', 'abierta')
        .maybeSingle();
      return { data, error };
    },
    [caja],
  );

  const { data: movimientos, refetch: refetchMovimientos } = useSupabaseQuery<MovimientoCajaRow[]>(
    async () => {
      if (!apertura) return { data: [], error: null };
      const { data, error } = await supabase
        .from('movimientos_caja')
        .select('*')
        .eq('apertura_id', apertura.id)
        .order('fecha', { ascending: false });
      return { data, error };
    },
    [apertura],
  );

  const { data: historial } = useSupabaseQuery<AperturaCajaRow[]>(
    async () => {
      if (!caja) return { data: [], error: null };
      const { data, error } = await supabase
        .from('aperturas_caja')
        .select('*')
        .eq('caja_id', caja.id)
        .eq('estado', 'cerrada')
        .order('fecha_apertura', { ascending: false })
        .limit(5);
      return { data, error };
    },
    [caja],
  );

  const ingresos = (movimientos ?? []).filter((m) => m.tipo === 'ingreso').reduce((s, m) => s + m.monto, 0);
  const egresos = (movimientos ?? []).filter((m) => m.tipo === 'egreso').reduce((s, m) => s + m.monto, 0);
  const saldoActual = (apertura?.base_inicial ?? 0) + ingresos - egresos;

  function alCambiarEstadoCaja() {
    setError(null);
    refetchCaja();
    refetchApertura();
    refetchMovimientos();
  }

  const cargando = cargandoCaja || cargandoApertura;

  return (
    <div>
      <PageHeader title="Caja" description="Apertura, movimientos y cierre con arqueo — DAF v2, sección 25" />

      {cargando && <p className="text-sm text-onyx/50">Cargando…</p>}

      {!cargando && !apertura && perfil && (
        <AbrirCajaForm salonId={perfil.salon_id} onAbierta={alCambiarEstadoCaja} onError={setError} />
      )}

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {!cargando && apertura && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-4">
            <Tarjeta titulo="Base inicial" valor={apertura.base_inicial} />
            <Tarjeta titulo="Ingresos" valor={ingresos} clase="text-green-700" />
            <Tarjeta titulo="Egresos" valor={egresos} clase="text-red-700" />
          </div>

          <div className="card mb-4">
            <p className="text-sm text-onyx/60">Saldo actual (base + ingresos − egresos)</p>
            <p className="font-display text-2xl text-onyx">${saldoActual.toLocaleString('es-CO')}</p>
            <p className="mt-1 text-xs text-onyx/40">
              Abierta el{' '}
              {new Date(apertura.fecha_apertura).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>

          {movimientos && movimientos.length > 0 && (
            <div className="card mb-4 overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-onyx/10 text-left text-onyx/50">
                    <th className="px-4 py-3 font-medium">Hora</th>
                    <th className="px-4 py-3 font-medium">Tipo</th>
                    <th className="px-4 py-3 font-medium">Origen</th>
                    <th className="px-4 py-3 font-medium">Descripción</th>
                    <th className="px-4 py-3 font-medium">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => (
                    <tr key={m.id} className="border-b border-onyx/5 last:border-0">
                      <td className="px-4 py-3 text-onyx/60">
                        {new Date(m.fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${m.tipo === 'ingreso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                          {m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                        </span>
                      </td>
                      <td className="px-4 py-3 capitalize text-onyx/70">{ORIGEN_LABEL[m.origen] ?? m.origen}</td>
                      <td className="px-4 py-3 text-onyx/60">{m.descripcion ?? '—'}</td>
                      <td className={`px-4 py-3 font-medium ${m.tipo === 'ingreso' ? 'text-green-700' : 'text-red-700'}`}>
                        {m.tipo === 'ingreso' ? '+' : '−'}${m.monto.toLocaleString('es-CO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <CerrarCajaForm aperturaId={apertura.id} saldoEsperado={saldoActual} onCerrada={alCambiarEstadoCaja} onError={setError} />
        </>
      )}

      {historial && historial.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-2 font-display text-base text-onyx">Cierres recientes</h3>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-onyx/10 text-left text-onyx/50">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Base</th>
                  <th className="px-4 py-3 font-medium">Esperado</th>
                  <th className="px-4 py-3 font-medium">Contado</th>
                  <th className="px-4 py-3 font-medium">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h) => (
                  <tr key={h.id} className="border-b border-onyx/5 last:border-0">
                    <td className="px-4 py-3 text-onyx/60">
                      {h.fecha_cierre && new Date(h.fecha_cierre).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-4 py-3 text-onyx/70">${h.base_inicial.toLocaleString('es-CO')}</td>
                    <td className="px-4 py-3 text-onyx/70">${(h.saldo_esperado ?? 0).toLocaleString('es-CO')}</td>
                    <td className="px-4 py-3 text-onyx/70">${(h.saldo_contado ?? 0).toLocaleString('es-CO')}</td>
                    <td className={`px-4 py-3 font-medium ${(h.diferencia ?? 0) === 0 ? 'text-onyx/60' : (h.diferencia ?? 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {(h.diferencia ?? 0) > 0 ? '+' : ''}
                      ${(h.diferencia ?? 0).toLocaleString('es-CO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const ORIGEN_LABEL: Record<string, string> = {
  venta: 'Venta',
  compra: 'Compra',
  gasto: 'Gasto',
  pago_empleado: 'Pago a empleado',
  otro: 'Otro',
};

function Tarjeta({ titulo, valor, clase }: { titulo: string; valor: number; clase?: string }) {
  return (
    <div className="card">
      <p className="text-xs text-onyx/50">{titulo}</p>
      <p className={`mt-1 font-display text-xl ${clase ?? 'text-onyx'}`}>${valor.toLocaleString('es-CO')}</p>
    </div>
  );
}

function AbrirCajaForm({
  salonId,
  onAbierta,
  onError,
}: {
  salonId: string;
  onAbierta: () => void;
  onError: (msg: string | null) => void;
}) {
  const [baseInicial, setBaseInicial] = useState('0');
  const [guardando, setGuardando] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onError(null);
    setGuardando(true);

    const { error: err } = await supabase.rpc('abrir_caja', {
      p_salon_id: salonId,
      p_base_inicial: Number(baseInicial) || 0,
    });

    setGuardando(false);

    if (err) {
      onError(err.message);
      return;
    }

    onAbierta();
  }

  return (
    <form onSubmit={handleSubmit} className="card max-w-sm space-y-4">
      <p className="text-sm text-onyx/70">No hay una caja abierta ahora mismo. Abre una para empezar a registrar el día.</p>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-onyx/80">Base inicial</span>
        <input
          type="number"
          min={0}
          step="0.01"
          className="input-field"
          value={baseInicial}
          onChange={(e) => setBaseInicial(e.target.value)}
        />
      </label>
      <button type="submit" disabled={guardando} className="btn-primary w-full">
        {guardando ? 'Abriendo…' : 'Abrir caja'}
      </button>
    </form>
  );
}

function CerrarCajaForm({
  aperturaId,
  saldoEsperado,
  onCerrada,
  onError,
}: {
  aperturaId: string;
  saldoEsperado: number;
  onCerrada: () => void;
  onError: (msg: string | null) => void;
}) {
  const [saldoContado, setSaldoContado] = useState(String(saldoEsperado));
  const [guardando, setGuardando] = useState(false);
  const [mostrar, setMostrar] = useState(false);

  const diferencia = (Number(saldoContado) || 0) - saldoEsperado;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onError(null);
    setGuardando(true);

    const { error: err } = await supabase.rpc('cerrar_caja', {
      p_apertura_id: aperturaId,
      p_saldo_contado: Number(saldoContado) || 0,
    });

    setGuardando(false);

    if (err) {
      onError(err.message);
      return;
    }

    onCerrada();
  }

  if (!mostrar) {
    return (
      <button type="button" className="btn-secondary" onClick={() => setMostrar(true)}>
        Cerrar caja
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card max-w-sm space-y-4">
      <p className="text-sm text-onyx/70">
        Cuenta el efectivo real en caja y regístralo abajo. El sistema compara contra lo que esperaba según los
        movimientos del día.
      </p>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-onyx/80">Saldo contado</span>
        <input
          type="number"
          step="0.01"
          className="input-field"
          value={saldoContado}
          onChange={(e) => setSaldoContado(e.target.value)}
        />
      </label>
      <p className={`text-sm ${diferencia === 0 ? 'text-onyx/60' : diferencia > 0 ? 'text-green-700' : 'text-red-700'}`}>
        Diferencia: {diferencia > 0 ? '+' : ''}${diferencia.toLocaleString('es-CO')}
      </p>
      <div className="flex gap-3">
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Cerrando…' : 'Confirmar cierre'}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setMostrar(false)}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
